import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import apiClient from "utils/apiClient";
import { getAuthToken } from "utils/authToken";
import { ENDPOINTS } from "utils/endpoints";
import Recipe1Page from "layouts/module/components/RecipeDetails";
import { RED, RED_LIGHT, BORDER, MUTED, GREEN, GREEN_LIGHT } from "assets/theme/brandColors";
import { fmtPantry } from "utils/ingredientFormatting";
import { MEAL_TYPE_BADGE as MEAL_TYPE_STYLE } from "utils/mealTypes";

// ── Warm cookbook palette (mirrors wireframe) ────────────────────────────────
const BG = "#e8e0d5";
const CARD = "#f0ebe2";
const CARD_ALT = "#e6dfd4";
const CB_BORDER = "#cec4b4";
const TEXT = "#211a12";
const CB_MUTED = "#7a6e5f";
const FAINT = "#b0a494";
const AMBER = "#7a5c00";
const AMBER_BG = "#ede0a8";
const AMBER_BDR = "#c4a830";

const NOTE_PROMPTS = [
  "What would you change next time?",
  "Who did you cook this for?",
  "What did you serve this with?",
  "Any tricks you discovered?",
  "How did it turn out?",
  "Would you make this again?",
  "Any substitutions that worked well?",
];

function randPrompt() {
  return NOTE_PROMPTS[Math.floor(Math.random() * NOTE_PROMPTS.length)];
}

function starsDisplay(rating) {
  if (!rating) return "—";
  return "★".repeat(rating) + "☆".repeat(5 - rating);
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// ── CookbookRecipeDetail ─────────────────────────────────────────────────────
function CookbookRecipeDetail({
  recipeId,
  onData,
  onFormattedIngredients,
  servings,
  onServingsChange,
}) {
  return (
    <Recipe1Page
      recipeId={recipeId}
      isCompleted={true}
      onMarkCompleted={() => {}}
      hideHeader={true}
      onData={onData}
      onFormattedIngredients={onFormattedIngredients}
      servings={servings}
      onServingsChange={onServingsChange}
    />
  );
}
CookbookRecipeDetail.propTypes = {
  recipeId: PropTypes.number.isRequired,
  onData: PropTypes.func,
  onFormattedIngredients: PropTypes.func,
  servings: PropTypes.number,
  onServingsChange: PropTypes.func,
};

// ── RecipeCard ───────────────────────────────────────────────────────────────
function RecipeCard({ recipe, onOpen, onCookAgain }) {
  return (
    <Box
      sx={{
        background: CARD,
        borderRadius: "13px",
        border: `1.5px solid ${CB_BORDER}`,
        p: "17px 19px",
        display: "flex",
        flexDirection: "column",
        gap: "9px",
        cursor: "pointer",
        position: "relative",
        transition: "box-shadow 0.15s, border-color 0.15s",
        "&:hover": { boxShadow: "0 3px 14px rgba(42,33,24,0.09)", borderColor: "#c4b8a8" },
      }}
      onClick={onOpen}
    >
      {MEAL_TYPE_STYLE[recipe.meal_type] && (
        <Box
          sx={{
            position: "absolute",
            top: "13px",
            right: "13px",
            fontSize: 10,
            fontWeight: 700,
            px: "6px",
            py: "2px",
            borderRadius: "4px",
            bgcolor: MEAL_TYPE_STYLE[recipe.meal_type].bg,
            color: MEAL_TYPE_STYLE[recipe.meal_type].color,
          }}
        >
          {MEAL_TYPE_STYLE[recipe.meal_type].label}
        </Box>
      )}
      <Box
        sx={{
          fontFamily: "Lora, Georgia, serif",
          fontSize: 14,
          fontWeight: 600,
          color: TEXT,
          lineHeight: 1.35,
          pr: MEAL_TYPE_STYLE[recipe.meal_type] ? "52px" : 0,
        }}
      >
        {recipe.title}
      </Box>
      <Box
        sx={{
          fontSize: 12,
          color: recipe.note ? CB_MUTED : FAINT,
          fontStyle: "italic",
          lineHeight: 1.5,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {recipe.note || "What do you remember about this one?"}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ color: "#c8972a", fontSize: 12, letterSpacing: 1 }}>
          {starsDisplay(recipe.rating)}
        </Box>
        {recipe.time_in_minutes && (
          <Box sx={{ fontSize: 11, color: FAINT }}>⏱ {recipe.time_in_minutes} min</Box>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: "7px" }} onClick={(e) => e.stopPropagation()}>
        <Box
          component="button"
          onClick={onOpen}
          sx={{
            padding: "6px 14px",
            background: RED,
            color: "#fff",
            border: "none",
            borderRadius: "7px",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "Roboto, sans-serif",
            transition: "background 0.15s",
            "&:hover": { background: "#7d1520" },
          }}
        >
          Open
        </Box>
        <Box
          component="button"
          onClick={onCookAgain}
          sx={{
            padding: "6px 12px",
            background: AMBER_BG,
            color: AMBER,
            border: `1.5px solid ${AMBER_BDR}`,
            borderRadius: "7px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "Roboto, sans-serif",
            transition: "background 0.15s",
            whiteSpace: "nowrap",
            "&:hover": { background: "#e3d080" },
          }}
        >
          🍳 Cook again
        </Box>
      </Box>
    </Box>
  );
}
RecipeCard.propTypes = {
  recipe: PropTypes.shape({
    recipe_id: PropTypes.number,
    title: PropTypes.string,
    note: PropTypes.string,
    rating: PropTypes.number,
    time_in_minutes: PropTypes.number,
    meal_type: PropTypes.string,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  onCookAgain: PropTypes.func.isRequired,
};

// ── ScheduleCard (cookbook version) ─────────────────────────────────────────
function CookbookScheduleCard({ program, onOpen, onCookAgain, onRemove }) {
  const pct = program.total_count > 0 ? (program.completed_count / program.total_count) * 100 : 0;
  const allDone = program.total_count > 0 && program.completed_count === program.total_count;
  return (
    <Box
      sx={{
        background: CARD,
        borderRadius: "13px",
        border: `1.5px solid ${CB_BORDER}`,
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: "17px 19px 13px", borderBottom: `1px solid ${CB_BORDER}` }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            mb: "10px",
          }}
        >
          <Box>
            <Box
              sx={{
                fontFamily: "Lora, Georgia, serif",
                fontSize: 15,
                fontWeight: 600,
                color: TEXT,
                lineHeight: 1.3,
              }}
            >
              {program.title}
            </Box>
            <Box
              sx={{ fontSize: 11, color: CB_MUTED, mt: "3px", fontFamily: "Roboto, sans-serif" }}
            >
              Saved {formatDate(program.saved_at)}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: "6px", flexShrink: 0 }}>
            <Box
              component="button"
              onClick={onCookAgain}
              sx={{
                padding: "6px 12px",
                background: AMBER_BG,
                color: AMBER,
                border: `1.5px solid ${AMBER_BDR}`,
                borderRadius: "7px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
                whiteSpace: "nowrap",
                "&:hover": { background: "#e3d080" },
              }}
            >
              🍳 Cook again
            </Box>
            <Box
              component="button"
              onClick={onOpen}
              sx={{
                padding: "6px 13px",
                background: RED,
                color: "#fff",
                border: "none",
                borderRadius: "7px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
                "&:hover": { background: "#7d1520" },
              }}
            >
              Open
            </Box>
            <Box
              component="button"
              onClick={onRemove}
              title="Remove from cookbook"
              sx={{
                width: 30,
                height: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1.5px solid ${CB_BORDER}`,
                borderRadius: "7px",
                background: CARD,
                cursor: "pointer",
                color: CB_MUTED,
                fontSize: 15,
                transition: "all 0.15s",
                "&:hover": { borderColor: "#e57373", color: "#e57373", background: "#fff5f5" },
              }}
            >
              ✕
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box
            sx={{ flex: 1, height: 5, background: CB_BORDER, borderRadius: 3, overflow: "hidden" }}
          >
            <Box
              sx={{
                width: `${pct}%`,
                height: "100%",
                background: allDone ? GREEN : RED,
                borderRadius: 3,
                transition: "width 0.4s",
              }}
            />
          </Box>
          <Box
            sx={{
              fontSize: 11,
              fontFamily: "Roboto, sans-serif",
              color: allDone ? GREEN : CB_MUTED,
              fontWeight: allDone ? 700 : 400,
              whiteSpace: "nowrap",
            }}
          >
            {program.completed_count} / {program.total_count} {allDone ? "✓" : "done"}
          </Box>
        </Box>
      </Box>
      <Box>
        {program.recipes.map((r, i) => (
          <Box
            key={r.recipe_id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              px: "19px",
              py: "8px",
              borderBottom: i < program.recipes.length - 1 ? `1px solid ${CARD_ALT}` : "none",
              fontSize: 13,
            }}
          >
            <Box
              component="span"
              sx={{ fontSize: 12, color: r.is_completed ? GREEN : FAINT, flexShrink: 0 }}
            >
              {r.is_completed ? "✓" : "○"}
            </Box>
            <Box
              sx={{
                flex: 1,
                color: r.is_completed ? CB_MUTED : TEXT,
                fontFamily: "Roboto, sans-serif",
              }}
            >
              {r.title}
            </Box>
            {r.rating && <Box sx={{ fontSize: 12, color: "#c8972a" }}>{"★".repeat(r.rating)}</Box>}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
CookbookScheduleCard.propTypes = {
  program: PropTypes.shape({
    program_id: PropTypes.number,
    title: PropTypes.string,
    saved_at: PropTypes.string,
    completed_count: PropTypes.number,
    total_count: PropTypes.number,
    recipes: PropTypes.array,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  onCookAgain: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

// ── Tiny servings stepper used inside CookAgainModal ────────────────────────
function ServingsStepper({ value, onChange, min = 1, max = 12 }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
      <Box
        component="button"
        onClick={(e) => {
          e.stopPropagation();
          onChange(Math.max(min, value - 1));
        }}
        disabled={value <= min}
        sx={{
          width: 24,
          height: 24,
          borderRadius: "6px",
          border: "1.5px solid",
          borderColor: value <= min ? "#e0e0e0" : "#ccc",
          bgcolor: "#fff",
          color: value <= min ? "#ccc" : "#555",
          fontSize: 15,
          lineHeight: 1,
          cursor: value <= min ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&:hover": value > min ? { borderColor: RED, color: RED } : {},
        }}
      >
        −
      </Box>
      <Box sx={{ fontSize: 13, fontWeight: 700, color: "#222", minWidth: 16, textAlign: "center" }}>
        {value}
      </Box>
      <Box
        component="button"
        onClick={(e) => {
          e.stopPropagation();
          onChange(Math.min(max, value + 1));
        }}
        disabled={value >= max}
        sx={{
          width: 24,
          height: 24,
          borderRadius: "6px",
          border: "1.5px solid",
          borderColor: value >= max ? "#e0e0e0" : "#ccc",
          bgcolor: "#fff",
          color: value >= max ? "#ccc" : "#555",
          fontSize: 15,
          lineHeight: 1,
          cursor: value >= max ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&:hover": value < max ? { borderColor: RED, color: RED } : {},
        }}
      >
        +
      </Box>
    </Box>
  );
}
ServingsStepper.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
};

// ── CookAgain modal ──────────────────────────────────────────────────────────
function CookAgainModal({ preSelected, allRecipes, onClose, onSubmit }) {
  const [selected, setSelected] = useState(new Set(preSelected.map((r) => r.recipe_id)));
  const [mealCount, setMealCount] = useState(Math.min(preSelected.length || 3, 7));
  const [schedName, setSchedName] = useState("");
  const [fillNew, setFillNew] = useState(true);
  const [newServings, setNewServings] = useState(2);
  // Per-recipe servings: { [recipe_id]: number }
  const [recipeServings, setRecipeServings] = useState(() =>
    Object.fromEntries(preSelected.map((r) => [r.recipe_id, 2]))
  );

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (!recipeServings[id]) setRecipeServings((s) => ({ ...s, [id]: 2 }));
      }
      return next;
    });

  const handleSubmit = () => {
    const recipeIds = [...selected];
    if (recipeIds.length === 0) return;
    onSubmit({ schedName, mealCount, recipeIds, fillNew, recipeServings, newServings });
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
      onClick={onClose}
    >
      <Box
        sx={{
          background: "#fff",
          borderRadius: "16px",
          p: "28px 28px 24px",
          maxWidth: 520,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          component="button"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 14,
            right: 16,
            background: "none",
            border: "none",
            fontSize: 20,
            cursor: "pointer",
            color: MUTED,
            lineHeight: 1,
          }}
        >
          ×
        </Box>
        <Box
          sx={{
            fontSize: 18,
            fontWeight: 800,
            color: "#222",
            mb: 1,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Cook again
        </Box>
        <Box sx={{ fontSize: 13, color: "#666", mb: "20px", fontFamily: "Roboto, sans-serif" }}>
          Choose which recipes to include and how many meals you want.
        </Box>

        <Box
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1px",
            color: MUTED,
            mb: "6px",
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Schedule name
        </Box>
        <Box
          component="input"
          value={schedName}
          onChange={(e) => setSchedName(e.target.value)}
          placeholder={
            preSelected.length === 1
              ? `${preSelected[0]?.title} — round 2`
              : "e.g. Italian Week II…"
          }
          sx={{
            width: "100%",
            padding: "9px 12px",
            border: "1.5px solid #e0e0e0",
            borderRadius: "9px",
            fontSize: 13,
            fontFamily: "Roboto, sans-serif",
            outline: "none",
            mb: "18px",
            "&:focus": { borderColor: RED },
          }}
        />

        <Box
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1px",
            color: MUTED,
            mb: "8px",
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Number of meals
        </Box>
        <Box sx={{ display: "flex", gap: "6px", mb: "20px", flexWrap: "wrap" }}>
          {[1, 2, 3, 4, 5, 6, 7].map((n) => (
            <Box
              key={n}
              component="button"
              onClick={() => setMealCount(n)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                border: "1.5px solid",
                borderColor: mealCount === n ? RED : "#e0e0e0",
                background: mealCount === n ? RED_LIGHT : "#fff",
                color: mealCount === n ? RED : "#666",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
              }}
            >
              {n}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1px",
            color: MUTED,
            mb: "8px",
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Recipes from your cookbook
        </Box>
        <Box
          sx={{
            border: "1.5px solid #e8e8e8",
            borderRadius: "10px",
            overflow: "hidden",
            mb: "16px",
          }}
        >
          {allRecipes.map((r, i) => {
            const isSel = selected.has(r.recipe_id);
            return (
              <Box key={r.recipe_id}>
                <Box
                  onClick={() => toggle(r.recipe_id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    px: "14px",
                    py: "10px",
                    borderBottom: i < allRecipes.length - 1 ? "1px solid #f5f5f5" : "none",
                    cursor: "pointer",
                    background: isSel ? RED_LIGHT : "#fff",
                    transition: "background 0.12s",
                  }}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "5px",
                      border: "1.5px solid",
                      borderColor: isSel ? RED : "#ccc",
                      background: isSel ? RED : "#fff",
                      color: "#fff",
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {isSel ? "✓" : ""}
                  </Box>
                  <Box
                    sx={{ flex: 1, fontSize: 13, fontFamily: "Roboto, sans-serif", color: "#222" }}
                  >
                    {r.title}
                  </Box>
                  <Box sx={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
                    {r.rating && (
                      <Box sx={{ fontSize: 12, color: "#c8972a" }}>{"★".repeat(r.rating)}</Box>
                    )}
                    {r.time_in_minutes && (
                      <Box sx={{ fontSize: 11, color: MUTED }}>⏱ {r.time_in_minutes} min</Box>
                    )}
                  </Box>
                </Box>
                {isSel && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: "14px",
                      py: "6px",
                      bgcolor: "#fafafa",
                      borderBottom: i < allRecipes.length - 1 ? "1px solid #f5f5f5" : "none",
                    }}
                  >
                    <Box sx={{ fontSize: 11, color: "#888", fontFamily: "Roboto, sans-serif" }}>
                      Servings
                    </Box>
                    <ServingsStepper
                      value={recipeServings[r.recipe_id] ?? 2}
                      onChange={(v) => setRecipeServings((s) => ({ ...s, [r.recipe_id]: v }))}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            mb: "22px",
            cursor: "pointer",
          }}
          onClick={() => setFillNew((v) => !v)}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: "4px",
              border: "1.5px solid",
              borderColor: fillNew ? RED : "#ccc",
              background: fillNew ? RED : "#fff",
              color: "#fff",
              fontSize: 11,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: "1px",
            }}
          >
            {fillNew ? "✓" : ""}
          </Box>
          <Box>
            <Box
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#222",
                fontFamily: "Roboto, sans-serif",
              }}
            >
              Fill remaining slots with new recipes
            </Box>
            <Box sx={{ fontSize: 12, color: MUTED, fontFamily: "Roboto, sans-serif" }}>
              We&apos;ll find similar recipes to round out your schedule
            </Box>
          </Box>
        </Box>

        {fillNew && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: "12px 14px",
              mb: "22px",
              bgcolor: "#f9f9f9",
              border: "1.5px solid #e8e8e8",
              borderRadius: "10px",
            }}
          >
            <Box>
              <Box
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#222",
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                Servings for new recipes
              </Box>
              <Box sx={{ fontSize: 11, color: MUTED, fontFamily: "Roboto, sans-serif" }}>
                Applied to any new recipes added to fill remaining slots
              </Box>
            </Box>
            <ServingsStepper value={newServings} onChange={setNewServings} />
          </Box>
        )}

        <Box sx={{ display: "flex", gap: "10px" }}>
          <Box
            component="button"
            onClick={handleSubmit}
            sx={{
              flex: 1,
              padding: "11px 0",
              background: RED,
              color: "#fff",
              border: "none",
              borderRadius: "9px",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "Roboto, sans-serif",
              "&:hover": { background: "#7d1520" },
            }}
          >
            Create schedule
          </Box>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              padding: "11px 20px",
              background: "#f0f0f0",
              color: "#444",
              border: "none",
              borderRadius: "9px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "Roboto, sans-serif",
              "&:hover": { background: "#e4e4e4" },
            }}
          >
            Cancel
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
CookAgainModal.propTypes = {
  preSelected: PropTypes.array.isRequired,
  allRecipes: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

// ── ConfirmModal ─────────────────────────────────────────────────────────────
function ConfirmModal({ title, sub, onConfirm, onClose }) {
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
      onClick={onClose}
    >
      <Box
        sx={{
          background: "#fff",
          borderRadius: "14px",
          p: "28px 24px 22px",
          maxWidth: 380,
          width: "100%",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            fontSize: 17,
            fontWeight: 800,
            color: "#222",
            mb: 1,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          {title}
        </Box>
        <Box
          sx={{
            fontSize: 13,
            color: "#666",
            mb: "22px",
            fontFamily: "Roboto, sans-serif",
            lineHeight: 1.6,
          }}
        >
          {sub}
        </Box>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Box
            component="button"
            onClick={onConfirm}
            sx={{
              flex: 1,
              padding: "10px 0",
              background: "#e53935",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "Roboto, sans-serif",
              "&:hover": { background: "#b71c1c" },
            }}
          >
            Yes, remove
          </Box>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              padding: "10px 18px",
              background: "#f0f0f0",
              color: "#222",
              border: "none",
              borderRadius: "8px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "Roboto, sans-serif",
              "&:hover": { background: "#e4e4e4" },
            }}
          >
            Cancel
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
ConfirmModal.propTypes = {
  title: PropTypes.string.isRequired,
  sub: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ── Main Cookbook component ──────────────────────────────────────────────────
export default function Cookbook() {
  const navigate = useNavigate();
  const location = useLocation();
  const locState = location.state || {};

  // View + selection derived from URL history state — enables browser back to work
  const view = locState.view || "home";
  const selectedRecipe = locState.selectedRecipe || null;
  const selectedProgram = locState.selectedProgram || null;

  const [loading, setLoading] = useState(true);
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [savedPrograms, setSavedPrograms] = useState([]);
  const [username, setUsername] = useState("");

  const [mainTab, setMainTab] = useState("recipe"); // "recipe" | "schedule"
  const [recipeFilter, setRecipeFilter] = useState("all"); // "all" | "az" | "top" | "mostmade"

  // Note editing state for recipe detail
  const [noteText, setNoteText] = useState("");
  const [noteEditing, setNoteEditing] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  // Loaded recipe data (ingredients, pantry, servings) from CookbookRecipeDetail
  const [recipeInfo, setRecipeInfo] = useState(null);
  const [cookbookServings, setCookbookServings] = useState(null);

  // Sync note text when the selected recipe changes (e.g. browser back/forward)
  useEffect(() => {
    if (selectedRecipe) {
      setNoteText(selectedRecipe.note || "");
      setNoteEditing(false);
      setRecipeInfo(null);
      setCookbookServings(null);
    }
  }, [selectedRecipe?.recipe_id]);

  // Cook Again modal
  const [cookAgainOpen, setCookAgainOpen] = useState(false);
  const [cookAgainPresel, setCookAgainPresel] = useState([]);
  const [submittingCookAgain, setSubmittingCookAgain] = useState(false);

  // Confirm remove modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // { type: "recipe"|"schedule", id }

  useEffect(() => {
    if (!getAuthToken()) {
      navigate("/authentication/sign-in", { replace: true });
      return;
    }
    (async () => {
      try {
        const [recRes, progRes, meRes] = await Promise.all([
          apiClient.get(ENDPOINTS.collections.savedRecipes),
          apiClient.get(ENDPOINTS.collections.savedPrograms),
          apiClient.get(ENDPOINTS.users.me),
        ]);
        setSavedRecipes(Array.isArray(recRes.data) ? recRes.data : []);
        setSavedPrograms(Array.isArray(progRes.data) ? progRes.data : []);
        setUsername(meRes.data?.username || "");
      } catch (err) {
        console.error("Cookbook load error:", err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  // ── All cookbook recipes: saved + every recipe in every saved schedule (deduped) ──
  const allCookbookRecipes = (() => {
    const map = new Map();
    // First pass: recipes from saved programs (lower priority)
    for (const prog of savedPrograms) {
      for (const r of prog.recipes) {
        if (!map.has(r.recipe_id)) {
          map.set(r.recipe_id, {
            recipe_id: r.recipe_id,
            title: r.title,
            time_in_minutes: r.time_in_minutes,
            rating: r.rating,
            note: null,
            saved_at: null,
            meal_type: r.meal_type,
          });
        }
      }
    }
    // Second pass: explicitly saved recipes override (they have notes + saved_at)
    for (const r of savedRecipes) {
      map.set(r.recipe_id, {
        recipe_id: r.recipe_id,
        title: r.title,
        time_in_minutes: r.time_in_minutes,
        rating: r.rating,
        note: r.note,
        saved_at: r.saved_at,
        meal_type: r.meal_type,
      });
    }
    return [...map.values()].sort((a, b) => a.title.localeCompare(b.title));
  })();

  // ── Computed stats ─────────────────────────────────────────────────────────
  const avgRating = (() => {
    const rated = allCookbookRecipes.filter((r) => r.rating);
    if (rated.length === 0) return null;
    return (rated.reduce((s, r) => s + r.rating, 0) / rated.length).toFixed(1);
  })();

  const mostMadeTitle = allCookbookRecipes.length > 0 ? allCookbookRecipes[0].title : null;

  // ── Filtered recipe list ────────────────────────────────────────────────────
  const filteredRecipes = (() => {
    let list = [...allCookbookRecipes];
    if (recipeFilter === "az") list = list.sort((a, b) => a.title.localeCompare(b.title));
    else if (recipeFilter === "top")
      list = list.filter((r) => r.rating).sort((a, b) => b.rating - a.rating);
    else if (recipeFilter === "meal") list = list.filter((r) => r.meal_type === "main");
    else if (recipeFilter === "snack") list = list.filter((r) => r.meal_type === "snack");
    else if (recipeFilter === "portable") list = list.filter((r) => r.meal_type === "portable");
    return list;
  })();

  // ── Handlers ───────────────────────────────────────────────────────────────
  const openRecipe = useCallback(
    (recipe) => {
      navigate("/cookbook", {
        state: {
          view: "recipe",
          selectedRecipe: recipe,
          selectedProgram: locState.selectedProgram,
        },
      });
    },
    [navigate, locState.selectedProgram]
  );

  const handleNoteSave = async () => {
    if (!selectedRecipe) return;
    setNoteSaving(true);
    try {
      await apiClient.post(ENDPOINTS.collections.rateRecipe(selectedRecipe.recipe_id), {
        rating: selectedRecipe.rating || 0,
        note: noteText || null,
      });
      const updatedRecipe = { ...selectedRecipe, note: noteText || null };
      setSavedRecipes((prev) =>
        prev.map((r) =>
          r.recipe_id === selectedRecipe.recipe_id ? { ...r, note: noteText || null } : r
        )
      );
      // Update the location state in-place so back button still works
      navigate("/cookbook", {
        state: { ...locState, selectedRecipe: updatedRecipe },
        replace: true,
      });
      setNoteEditing(false);
    } catch (err) {
      console.error("Note save error:", err.message);
    } finally {
      setNoteSaving(false);
    }
  };

  const openSchedule = useCallback(
    (program) => {
      navigate("/cookbook", { state: { view: "schedule", selectedProgram: program } });
    },
    [navigate]
  );

  const goHome = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const openCookAgain = (presel) => {
    setCookAgainPresel(presel);
    setCookAgainOpen(true);
  };

  const handleCookAgainSubmit = async ({
    schedName,
    mealCount,
    recipeIds,
    fillNew,
    recipeServings,
    newServings,
  }) => {
    setSubmittingCookAgain(true);
    try {
      const res = await apiClient.post(ENDPOINTS.schedule.build, {
        title: schedName || "",
        meal_count: mealCount,
        recipe_ids: recipeIds,
        fill_new: fillNew,
        // Recorded on the schedule so a recipe swapped into this week later
        // inherits it, rather than falling back to its own serving count.
        ...(newServings ? { target_servings: newServings } : {}),
      });
      const pid = res.data.program_id;
      // Persist per-recipe servings; new recipes will be seeded from newServings via default key
      if (recipeServings && Object.keys(recipeServings).length > 0) {
        const map = {};
        recipeIds.forEach((id) => {
          map[String(id)] = recipeServings[id] ?? newServings ?? 2;
        });
        localStorage.setItem(`estia_servings_${pid}`, JSON.stringify(map));
      }
      if (newServings) {
        localStorage.setItem(`estia_servings_default_${pid}`, String(newServings));
      }
      setCookAgainOpen(false);
      navigate(`/module/${pid}`, { replace: true, state: { from: "Cookbook" } });
    } catch (err) {
      console.error("Cook again error:", err.message);
    } finally {
      setSubmittingCookAgain(false);
    }
  };

  const askRemove = (type, id) => {
    setConfirmTarget({ type, id });
    setConfirmOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!confirmTarget) return;
    const { type, id } = confirmTarget;
    try {
      // Both unsaves set a flag to one fixed value, so apiClient may repeat
      // them after a dropped connection without changing the outcome.
      if (type === "recipe") {
        await apiClient.delete(ENDPOINTS.collections.recipeSave(id), { idempotent: true });
        setSavedRecipes((prev) => prev.filter((r) => r.recipe_id !== id));
        if (selectedRecipe?.recipe_id === id) navigate("/cookbook", { replace: true });
      } else {
        await apiClient.delete(ENDPOINTS.collections.programSave(id), { idempotent: true });
        setSavedPrograms((prev) => prev.filter((p) => p.program_id !== id));
        if (selectedProgram?.program_id === id) navigate("/cookbook", { replace: true });
      }
    } catch (err) {
      console.error("Remove error:", err.message);
    } finally {
      setConfirmOpen(false);
      setConfirmTarget(null);
    }
  };

  // ── Breadcrumb ─────────────────────────────────────────────────────────────
  const Breadcrumb = ({ label }) => (
    <Box
      onClick={goHome}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontSize: 13,
        color: CB_MUTED,
        mb: "24px",
        cursor: "pointer",
        fontFamily: "Roboto, sans-serif",
      }}
    >
      <Box sx={{ color: RED, fontSize: 15 }}>←</Box>
      <Box sx={{ color: RED, fontWeight: 500, "&:hover": { textDecoration: "underline" } }}>
        Cookbook
      </Box>
      <Box sx={{ color: FAINT }}>/</Box>
      <Box sx={{ color: CB_MUTED }}>{label}</Box>
    </Box>
  );
  Breadcrumb.propTypes = { label: PropTypes.string.isRequired };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box
        sx={{
          background: BG,
          minHeight: "calc(100vh - 80px)",
          pt: 3,
          pb: 8,
          px: { xs: 2, md: 3 },
          borderRadius: "20px",
          border: `1.5px solid ${CB_BORDER}`,
        }}
      >
        {/* ── Loading ── */}
        {loading && (
          <Box sx={{ textAlign: "center", pt: 10 }}>
            <CircularProgress sx={{ color: RED }} />
          </Box>
        )}

        {/* ── HOME VIEW ── */}
        {!loading && view === "home" && (
          <>
            {/* Page title */}
            <Box
              sx={{
                fontFamily: "Lora, Georgia, serif",
                fontSize: 26,
                fontWeight: 600,
                color: TEXT,
                mb: "4px",
              }}
            >
              My <em style={{ fontStyle: "italic", color: RED }}>Cookbook</em>
            </Box>

            {/* Cover card */}
            <Box
              sx={{
                background: CARD,
                borderRadius: "16px",
                border: `1.5px solid ${CB_BORDER}`,
                p: "24px 28px",
                mb: "28px",
                mt: "16px",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "20px",
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Box
                  sx={{
                    fontFamily: "Lora, Georgia, serif",
                    fontSize: 20,
                    fontWeight: 600,
                    color: TEXT,
                    lineHeight: 1.35,
                    mb: "6px",
                  }}
                >
                  {username ? `Welcome back, ${username}.` : "Welcome back."}{" "}
                  {mostMadeTitle && (
                    <em style={{ fontStyle: "italic", color: RED }}>{mostMadeTitle}</em>
                  )}
                  {mostMadeTitle && " is in your collection."}
                </Box>
                <Box
                  sx={{
                    fontSize: 13,
                    color: CB_MUTED,
                    lineHeight: 1.6,
                    maxWidth: 480,
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  {allCookbookRecipes.length > 0
                    ? `${allCookbookRecipes.length} recipe${
                        allCookbookRecipes.length > 1 ? "s" : ""
                      } across ${savedPrograms.length} saved schedule${
                        savedPrograms.length !== 1 ? "s" : ""
                      }.${avgRating ? ` Average rating: ${avgRating} ★` : ""}`
                    : "Nothing here yet. Add a recipe while you're cooking it, or save a whole schedule — nothing lands here on its own."}
                </Box>
              </Box>
              {allCookbookRecipes.length > 0 && (
                <Box sx={{ display: "flex", gap: "20px", flexShrink: 0 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      sx={{
                        fontFamily: "Lora, Georgia, serif",
                        fontSize: 28,
                        fontWeight: 600,
                        color: RED,
                        lineHeight: 1,
                      }}
                    >
                      {allCookbookRecipes.length}
                    </Box>
                    <Box
                      sx={{
                        fontSize: 10,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        color: FAINT,
                        mt: "3px",
                        fontWeight: 600,
                      }}
                    >
                      Recipes
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      sx={{
                        fontFamily: "Lora, Georgia, serif",
                        fontSize: 28,
                        fontWeight: 600,
                        color: RED,
                        lineHeight: 1,
                      }}
                    >
                      {savedPrograms.length}
                    </Box>
                    <Box
                      sx={{
                        fontSize: 10,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        color: FAINT,
                        mt: "3px",
                        fontWeight: 600,
                      }}
                    >
                      Schedules
                    </Box>
                  </Box>
                  {avgRating && (
                    <Box sx={{ textAlign: "center" }}>
                      <Box
                        sx={{
                          fontFamily: "Lora, Georgia, serif",
                          fontSize: 28,
                          fontWeight: 600,
                          color: RED,
                          lineHeight: 1,
                        }}
                      >
                        {avgRating}
                      </Box>
                      <Box
                        sx={{
                          fontSize: 10,
                          textTransform: "uppercase",
                          letterSpacing: "1px",
                          color: FAINT,
                          mt: "3px",
                          fontWeight: 600,
                        }}
                      >
                        Avg ★
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Toggle + search row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                mb: "24px",
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  border: `1.5px solid ${CB_BORDER}`,
                  borderRadius: "10px",
                  overflow: "hidden",
                  background: CARD,
                  flexShrink: 0,
                }}
              >
                {["recipe", "schedule"].map((tab) => (
                  <Box
                    key={tab}
                    component="button"
                    onClick={() => setMainTab(tab)}
                    sx={{
                      padding: "8px 20px",
                      fontSize: 13,
                      fontWeight: 600,
                      border: "none",
                      background: mainTab === tab ? RED : "none",
                      color: mainTab === tab ? "#fff" : CB_MUTED,
                      cursor: "pointer",
                      fontFamily: "Roboto, sans-serif",
                      transition: "background 0.12s, color 0.12s",
                      "&:hover": mainTab !== tab ? { background: CARD_ALT, color: TEXT } : {},
                    }}
                  >
                    {tab === "recipe" ? "By Recipe" : "By Schedule"}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* ── By Recipe ── */}
            {mainTab === "recipe" && (
              <>
                {/* Filter pills */}
                {allCookbookRecipes.length > 0 && (
                  <Box sx={{ display: "flex", gap: "7px", flexWrap: "wrap", mb: "20px" }}>
                    {[
                      { key: "all", label: "All" },
                      { key: "az", label: "A – Z" },
                      { key: "top", label: "⭐ Top rated" },
                      { key: "meal", label: "Main" },
                      { key: "snack", label: "Snack" },
                      { key: "portable", label: "To-go" },
                    ].map((f) => (
                      <Box
                        key={f.key}
                        component="button"
                        onClick={() => setRecipeFilter(f.key)}
                        sx={{
                          padding: "5px 14px",
                          borderRadius: "20px",
                          fontSize: 12,
                          fontWeight: 600,
                          border: `1.5px solid`,
                          borderColor: recipeFilter === f.key ? RED : CB_BORDER,
                          background: recipeFilter === f.key ? RED : CARD,
                          color: recipeFilter === f.key ? "#fff" : CB_MUTED,
                          cursor: "pointer",
                          fontFamily: "Roboto, sans-serif",
                          transition: "all 0.12s",
                          "&:hover": recipeFilter !== f.key ? { borderColor: RED, color: RED } : {},
                        }}
                      >
                        {f.label}
                      </Box>
                    ))}
                  </Box>
                )}
                {allCookbookRecipes.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 10,
                      background: CARD,
                      borderRadius: "14px",
                      border: `1.5px solid ${CB_BORDER}`,
                    }}
                  >
                    <Box sx={{ fontSize: 36, mb: 2 }}>📖</Box>
                    <Box
                      sx={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: TEXT,
                        mb: 0.75,
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      No recipes yet
                    </Box>
                    <Box sx={{ fontSize: 13, color: CB_MUTED, fontFamily: "Roboto, sans-serif" }}>
                      Cook a recipe, then use <strong>Add to Cookbook</strong> on it. Recipes from
                      schedules you save land here too.
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(268px, 1fr))",
                      gap: "13px",
                    }}
                  >
                    {filteredRecipes.map((r) => (
                      <RecipeCard
                        key={r.recipe_id}
                        recipe={r}
                        onOpen={() => openRecipe(r)}
                        onCookAgain={() => openCookAgain([r])}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}

            {/* ── By Schedule ── */}
            {mainTab === "schedule" && (
              <>
                {savedPrograms.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 10,
                      background: CARD,
                      borderRadius: "14px",
                      border: `1.5px solid ${CB_BORDER}`,
                    }}
                  >
                    <Box sx={{ fontSize: 36, mb: 2 }}>📋</Box>
                    <Box
                      sx={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: TEXT,
                        mb: 0.75,
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      No schedules saved yet
                    </Box>
                    <Box sx={{ fontSize: 13, color: CB_MUTED, fontFamily: "Roboto, sans-serif" }}>
                      Use <strong>Save schedule</strong> in the cooking view — or the button in the
                      moment you finish one — and it will appear here.
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                      gap: "13px",
                    }}
                  >
                    {savedPrograms.map((p) => (
                      <CookbookScheduleCard
                        key={p.program_id}
                        program={p}
                        onOpen={() => openSchedule(p)}
                        onCookAgain={() => openCookAgain(p.recipes)}
                        onRemove={() => askRemove("schedule", p.program_id)}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}
          </>
        )}

        {/* ── RECIPE DETAIL VIEW ── */}
        {!loading && view === "recipe" && selectedRecipe && (
          <>
            <Breadcrumb label={selectedRecipe.title} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "280px 1fr" },
                gap: "28px",
                alignItems: "start",
              }}
            >
              {/* Left panel */}
              <Box sx={{ position: { md: "sticky" }, top: 28 }}>
                <Box
                  sx={{
                    background: CARD,
                    borderRadius: "14px",
                    border: `1.5px solid ${CB_BORDER}`,
                    p: "22px",
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: "Lora, Georgia, serif",
                      fontSize: 19,
                      fontWeight: 600,
                      color: TEXT,
                      lineHeight: 1.3,
                      mb: "14px",
                    }}
                  >
                    {selectedRecipe.title}
                  </Box>

                  {selectedRecipe.rating && (
                    <>
                      <Box
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "1.1px",
                          color: FAINT,
                          mb: "6px",
                        }}
                      >
                        Your rating
                      </Box>
                      <Box sx={{ fontSize: 22, color: "#c8972a", mb: "14px", letterSpacing: 2 }}>
                        {"★".repeat(selectedRecipe.rating)}
                        {"☆".repeat(5 - selectedRecipe.rating)}
                      </Box>
                    </>
                  )}

                  {/* Divider */}
                  <Box sx={{ borderTop: `1px solid ${CB_BORDER}`, my: "14px" }} />

                  {/* Personal note */}
                  <Box
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "1.1px",
                      color: FAINT,
                      mb: "8px",
                    }}
                  >
                    Personal note
                  </Box>
                  {!noteEditing ? (
                    <>
                      <Box
                        sx={{
                          fontFamily: "Lora, Georgia, serif",
                          fontSize: 13,
                          color: selectedRecipe.note ? "#5a4d3e" : FAINT,
                          fontStyle: "italic",
                          lineHeight: 1.75,
                          mb: "8px",
                        }}
                      >
                        {selectedRecipe.note || "What do you remember about this one?"}
                      </Box>
                      <Box
                        component="button"
                        onClick={() => {
                          setNoteText(selectedRecipe.note || "");
                          setNoteEditing(true);
                        }}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "4px 11px",
                          border: `1.5px solid ${CB_BORDER}`,
                          borderRadius: "20px",
                          fontSize: 11,
                          fontWeight: 600,
                          color: CB_MUTED,
                          cursor: "pointer",
                          background: CARD,
                          fontFamily: "Roboto, sans-serif",
                          transition: "all 0.12s",
                          "&:hover": { borderColor: RED, color: RED },
                        }}
                      >
                        ✏ {selectedRecipe.note ? "Edit note" : "Add note"}
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ mt: "4px" }}>
                      <Box
                        component="textarea"
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        rows={4}
                        placeholder="What would you change next time?"
                        sx={{
                          width: "100%",
                          padding: "9px 12px",
                          border: `1.5px solid ${CB_BORDER}`,
                          borderRadius: "9px",
                          fontSize: 13,
                          fontFamily: "Lora, Georgia, serif",
                          fontStyle: "italic",
                          color: "#5a4d3e",
                          background: CARD_ALT,
                          resize: "vertical",
                          outline: "none",
                          lineHeight: 1.75,
                          "&:focus": { borderColor: RED },
                        }}
                      />
                      <Box sx={{ display: "flex", gap: "7px", mt: "8px" }}>
                        <Box
                          component="button"
                          onClick={handleNoteSave}
                          disabled={noteSaving}
                          sx={{
                            padding: "6px 16px",
                            background: RED,
                            color: "#fff",
                            border: "none",
                            borderRadius: "7px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                            fontFamily: "Roboto, sans-serif",
                          }}
                        >
                          Save
                        </Box>
                        <Box
                          component="button"
                          onClick={() => {
                            setNoteText(selectedRecipe.note || "");
                            setNoteEditing(false);
                          }}
                          sx={{
                            padding: "6px 14px",
                            background: CARD_ALT,
                            color: CB_MUTED,
                            border: "none",
                            borderRadius: "7px",
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: "pointer",
                            fontFamily: "Roboto, sans-serif",
                          }}
                        >
                          Cancel
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Divider + Servings */}
                  {recipeInfo && (
                    <>
                      <Box sx={{ borderTop: `1px solid ${CB_BORDER}`, my: "14px" }} />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: "14px",
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "1.1px",
                            color: FAINT,
                          }}
                        >
                          Servings
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Box
                            component="button"
                            onClick={() => setCookbookServings((v) => Math.max(1, (v || 2) - 1))}
                            sx={{
                              width: 26,
                              height: 26,
                              borderRadius: "7px",
                              border: `1.5px solid ${CB_BORDER}`,
                              bgcolor: CARD_ALT,
                              color: CB_MUTED,
                              fontSize: 16,
                              lineHeight: 1,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              "&:hover": { borderColor: RED, color: RED },
                            }}
                          >
                            −
                          </Box>
                          <Box
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: TEXT,
                              minWidth: 20,
                              textAlign: "center",
                            }}
                          >
                            {cookbookServings || 2}
                          </Box>
                          <Box
                            component="button"
                            onClick={() => setCookbookServings((v) => Math.min(12, (v || 2) + 1))}
                            sx={{
                              width: 26,
                              height: 26,
                              borderRadius: "7px",
                              border: `1.5px solid ${CB_BORDER}`,
                              bgcolor: CARD_ALT,
                              color: CB_MUTED,
                              fontSize: 16,
                              lineHeight: 1,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              "&:hover": { borderColor: RED, color: RED },
                            }}
                          >
                            +
                          </Box>
                        </Box>
                      </Box>

                      {/* Ingredients */}
                      {(recipeInfo.fmtIngredients || []).length > 0 && (
                        <>
                          <Box
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "1.1px",
                              color: FAINT,
                              mb: "8px",
                            }}
                          >
                            Ingredients
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                              mb: "10px",
                            }}
                          >
                            {recipeInfo.fmtIngredients.map((ing, i) => (
                              <Box
                                key={i}
                                sx={{
                                  fontSize: 12,
                                  color: TEXT,
                                  fontFamily: "Roboto, sans-serif",
                                  py: "3px",
                                  borderBottom: `1px solid ${CARD_ALT}`,
                                }}
                              >
                                {ing}
                              </Box>
                            ))}
                          </Box>
                        </>
                      )}

                      {/* Pantry items */}
                      {(recipeInfo.pantryItems || []).length > 0 && (
                        <>
                          <Box
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "1.1px",
                              color: FAINT,
                              mb: "8px",
                            }}
                          >
                            From your pantry
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                              mb: "10px",
                            }}
                          >
                            {recipeInfo.pantryItems.map((item, i) => (
                              <Box
                                key={i}
                                sx={{
                                  fontSize: 12,
                                  color: AMBER,
                                  fontFamily: "Roboto, sans-serif",
                                  fontStyle: "italic",
                                  py: "3px",
                                  borderBottom: `1px solid ${CARD_ALT}`,
                                }}
                              >
                                {typeof item === "string" ? item : fmtPantry(item) || item.name}
                              </Box>
                            ))}
                          </Box>
                        </>
                      )}
                    </>
                  )}

                  {/* Divider + Remove */}
                  <Box sx={{ borderTop: `1px solid ${CB_BORDER}`, my: "16px" }} />
                  <Box
                    component="button"
                    onClick={() => askRemove("recipe", selectedRecipe.recipe_id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      width: "100%",
                      padding: "8px",
                      border: `1.5px solid ${CB_BORDER}`,
                      borderRadius: "9px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: CB_MUTED,
                      cursor: "pointer",
                      background: CARD,
                      fontFamily: "Roboto, sans-serif",
                      transition: "all 0.12s",
                      "&:hover": {
                        borderColor: "#e57373",
                        color: "#e57373",
                        background: "#fff5f5",
                      },
                    }}
                  >
                    🔖 Remove from cookbook
                  </Box>
                </Box>
              </Box>

              {/* Right panel — recordings */}
              <Box>
                <CookbookRecipeDetail
                  recipeId={selectedRecipe.recipe_id}
                  onData={({ recipeData: rd }) => {
                    setRecipeInfo(rd);
                    setCookbookServings(rd.recipeServings || 2);
                  }}
                  onFormattedIngredients={(ings, svgs) => {
                    setRecipeInfo((prev) => (prev ? { ...prev, fmtIngredients: ings } : prev));
                    setCookbookServings(svgs);
                  }}
                  servings={cookbookServings}
                  onServingsChange={setCookbookServings}
                />
              </Box>
            </Box>
          </>
        )}

        {/* ── SCHEDULE DETAIL VIEW ── */}
        {!loading && view === "schedule" && selectedProgram && (
          <>
            <Breadcrumb label={selectedProgram.title} />

            {/* Schedule header */}
            <Box
              sx={{
                background: CARD,
                borderRadius: "14px",
                border: `1.5px solid ${CB_BORDER}`,
                p: "20px 24px",
                mb: "20px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                  mb: "14px",
                }}
              >
                <Box>
                  <Box
                    sx={{
                      fontFamily: "Lora, Georgia, serif",
                      fontSize: 20,
                      fontWeight: 600,
                      color: TEXT,
                      mb: "3px",
                    }}
                  >
                    {selectedProgram.title}
                  </Box>
                  <Box sx={{ fontSize: 12, color: CB_MUTED, fontFamily: "Roboto, sans-serif" }}>
                    Saved {formatDate(selectedProgram.saved_at)}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                  <Box
                    component="button"
                    onClick={() => {
                      handleCookAgainSubmit({
                        schedName: `${selectedProgram.title} II`,
                        mealCount: selectedProgram.total_count,
                        recipeIds: selectedProgram.recipes.map((r) => r.recipe_id),
                        fillNew: false,
                        recipeServings: Object.fromEntries(
                          selectedProgram.recipes.map((r) => [r.recipe_id, 2])
                        ),
                        newServings: 2,
                      });
                    }}
                    sx={{
                      padding: "8px 16px",
                      background: RED,
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "Roboto, sans-serif",
                      "&:hover": { background: "#7d1520" },
                    }}
                  >
                    ＋ Cook this again
                  </Box>
                  <Box
                    component="button"
                    onClick={() => openCookAgain(selectedProgram.recipes)}
                    sx={{
                      padding: "8px 14px",
                      background: AMBER_BG,
                      color: AMBER,
                      border: `1.5px solid ${AMBER_BDR}`,
                      borderRadius: "8px",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "Roboto, sans-serif",
                      "&:hover": { background: "#e3d080" },
                    }}
                  >
                    🍳 Customise &amp; cook
                  </Box>
                  <Box
                    component="button"
                    onClick={() => askRemove("schedule", selectedProgram.program_id)}
                    sx={{
                      padding: "7px 14px",
                      background: "#fff",
                      color: "#e53935",
                      border: "1.5px solid #e57373",
                      borderRadius: "8px",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "Roboto, sans-serif",
                      "&:hover": { background: "#fff5f5" },
                    }}
                  >
                    Remove
                  </Box>
                </Box>
              </Box>

              {/* Meta pills */}
              <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {[
                  {
                    label: `${selectedProgram.completed_count} / ${selectedProgram.total_count} completed`,
                    green: selectedProgram.completed_count === selectedProgram.total_count,
                  },
                  { label: `${selectedProgram.total_count} recipes` },
                  ...(() => {
                    const rated = selectedProgram.recipes.filter((r) => r.rating);
                    if (rated.length === 0) return [];
                    const avg = (rated.reduce((s, r) => s + r.rating, 0) / rated.length).toFixed(1);
                    return [{ label: `Avg rating ${avg} ★` }];
                  })(),
                ].map((pill, i) => (
                  <Box
                    key={i}
                    sx={{
                      padding: "4px 12px",
                      borderRadius: "20px",
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: "Roboto, sans-serif",
                      background: pill.green ? GREEN_LIGHT : CARD_ALT,
                      color: pill.green ? GREEN : CB_MUTED,
                      border: `1px solid ${pill.green ? "#b2dfdb" : CB_BORDER}`,
                    }}
                  >
                    {pill.label}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Recipe rows */}
            <Box
              sx={{
                background: CARD,
                borderRadius: "14px",
                border: `1.5px solid ${CB_BORDER}`,
                overflow: "hidden",
              }}
            >
              {selectedProgram.recipes.map((r, i) => (
                <Box
                  key={r.recipe_id}
                  onClick={() =>
                    openRecipe({
                      recipe_id: r.recipe_id,
                      title: r.title,
                      rating: r.rating,
                      time_in_minutes: r.time_in_minutes,
                    })
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    px: "20px",
                    py: "14px",
                    borderBottom:
                      i < selectedProgram.recipes.length - 1 ? `1px solid ${CARD_ALT}` : "none",
                    cursor: "pointer",
                    transition: "background 0.12s",
                    "&:hover": { background: CARD_ALT },
                  }}
                >
                  <Box
                    component="span"
                    sx={{ fontSize: 13, color: r.is_completed ? GREEN : FAINT, flexShrink: 0 }}
                  >
                    {r.is_completed ? "✓" : "○"}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        fontSize: 14,
                        fontFamily: "Roboto, sans-serif",
                        color: TEXT,
                        fontWeight: 500,
                      }}
                    >
                      {r.title}
                    </Box>
                    {r.time_in_minutes && (
                      <Box
                        sx={{
                          fontSize: 11,
                          color: FAINT,
                          fontFamily: "Roboto, sans-serif",
                          mt: "2px",
                        }}
                      >
                        ⏱ {r.time_in_minutes} min
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: "10px", alignItems: "center", flexShrink: 0 }}>
                    {r.rating && (
                      <Box sx={{ fontSize: 13, color: "#c8972a" }}>{"★".repeat(r.rating)}</Box>
                    )}
                    <Box sx={{ fontSize: 13, color: FAINT }}>→</Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* ── Cook Again modal ── */}
      {cookAgainOpen && !submittingCookAgain && (
        <CookAgainModal
          preSelected={cookAgainPresel}
          allRecipes={allCookbookRecipes}
          onClose={() => setCookAgainOpen(false)}
          onSubmit={handleCookAgainSubmit}
        />
      )}

      {/* ── Confirm remove modal ── */}
      {confirmOpen && (
        <ConfirmModal
          title={
            confirmTarget?.type === "recipe"
              ? "Remove recipe from cookbook?"
              : "Remove schedule from cookbook?"
          }
          sub={
            confirmTarget?.type === "recipe"
              ? "This recipe will be removed. Your notes and ratings are kept."
              : "This schedule will be removed from your cookbook. Completion history stays intact."
          }
          onConfirm={handleConfirmRemove}
          onClose={() => {
            setConfirmOpen(false);
            setConfirmTarget(null);
          }}
        />
      )}
    </DashboardLayout>
  );
}
